import ReactGA from 'react-ga';
import baseClient from 'src/client.webstore/api/base';
import _ from 'lodash';
import productsClient from 'src/client.webstore/api/product';

// Allowlists transcribed from the TRAC iQ schemas so ecommerce link events emit
// ONLY their schema variables (PSSD-3724). window.utag_data carries page-level
// globals (customer_*, product_list_*, page_sub_type, order_*, ...) that must not
// ride along on these events. Keep in sync with:
//   ecommerce_part_cart_action  — Confluence page 4395073572
//   ecommerce_part_modify_cart  — Confluence page 4395073591
export const ECOMM_CART_ACTION_KEYS = [
    'ga4_id', 'site_id',
    'ecomm_cart_grand_total', 'ecomm_cart_shipping', 'ecomm_cart_subtotal', 'ecomm_cart_tax',
    'ecomm_cart_transaction_id', 'ecomm_part_currency_code',
    'ecomm_part_list_brands', 'ecomm_part_list_categories', 'ecomm_part_list_group_ids',
    'ecomm_part_list_ids', 'ecomm_part_list_names', 'ecomm_part_list_original_prices',
    'ecomm_part_list_quantities', 'ecomm_part_list_inventory_class',
    'ecomm_cart_event',
    // PSSD-3458 / PSSD-3457 — checkout_version (stamped by Analytics) + payment_type must survive
    // the _.pick here. Additive; needs no edit to the legacy file.
    'checkout_version', 'payment_type',
    'page_h1', 'page_title', 'page_title_count',
    'site_company_city', 'site_company_name', 'site_company_state', 'site_company_zip_code',
    'site_internal_id', 'site_phone_main', 'site_platform', 'site_section',
    'tealium_event'
];

export const ECOMM_MODIFY_CART_KEYS = [
    'ga4_id', 'site_id',
    'ecomm_part_currency_code', 'ecomm_cart_quantity_added_removed',
    'ecomm_part_detail_price', 'ecomm_part_detail_group_id', 'ecomm_part_detail_id',
    'ecomm_part_detail_model', 'ecomm_part_detail_name', 'ecomm_part_detail_variant',
    'ecomm_part_detail_inventory_class',
    'ecomm_cart_event',
    // PSSD-3458 (AC-1b.4) — add_to_cart / remove_from_cart carry checkout_version (stamped by
    // Analytics.action), so cart.js needs no edit.
    'checkout_version',
    'page_h1', 'page_title', 'page_title_count',
    'site_company_city', 'site_company_name', 'site_company_state', 'site_company_zip_code',
    'site_internal_id', 'site_phone_main', 'site_platform', 'site_section', 'site_sub_section',
    'tealium_event'
];

// ecommerce_part_cart_action — one-page-checkout action events (LAT-1306 / PSSD-3751; the event
// name was corrected from the undocumented ecommerce_checkout_action in PSSD-3786). The
// cart_action list vars + totals + the new checkout attributes + the same page/site globals the
// sibling ecommerce events keep. payment_type rides from window.utag_data.
//
// NOTE: if the data team's live iQ rules for this event read a global that is not listed here
// (e.g. customer_id), _.pick drops it. Reconcile against their final variable list before ship —
// see the plan's Validation step 4.
export const ECOMM_CHECKOUT_ACTION_KEYS = [
    'ga4_id', 'site_id',
    'ecomm_cart_grand_total', 'ecomm_cart_shipping', 'ecomm_cart_subtotal', 'ecomm_cart_tax',
    'ecomm_cart_transaction_id', 'ecomm_part_currency_code',
    'ecomm_part_list_brands', 'ecomm_part_list_categories', 'ecomm_part_list_group_ids',
    'ecomm_part_list_ids', 'ecomm_part_list_names', 'ecomm_part_list_original_prices',
    'ecomm_part_list_quantities', 'ecomm_part_list_inventory_class',
    'checkout_version', 'payment_type',
    'ecomm_checkout_action_name', 'ecomm_checkout_action_selection',
    'page_h1', 'page_title', 'page_title_count',
    'site_company_city', 'site_company_name', 'site_company_state', 'site_company_zip_code',
    'site_internal_id', 'site_phone_main', 'site_platform', 'site_section',
    'tealium_event'
];

// PSSD-3854 — every PSS event is one-page checkout (2.0). Stamped here, on the emitters, so it
// also rides on host pages that load Webstore.js without mounting the webstore (OEM part finder
// add-to-cart, cart side sheet). Applied before any allowlist, after the utag_data spread.
const CHECKOUT_VERSION = '2.0';

// Also keep it on the data layer itself, so the host's own page_view (which reads utag_data
// directly, outside Analytics) still carries it where the webstore mounts.
export const stampCheckoutVersion = () => {
    if (typeof window !== 'undefined' && window.utag_data) {
        window.utag_data.checkout_version = CHECKOUT_VERSION;
    }
};

class Analytics {
    trackers = [];

    constructor() {
        this.getGoogleAnalytics = _.once(this.getGoogleAnalytics);
    }

    getGoogleAnalytics = () => {
        this.trackers.push({
            trackingId: process.env.NODE_ENV === 'production' ? 'UA-40544695-4' : 'UA-40544695-6',
            gaOptions: {
                name: 'PSS'
            }
        });

        return baseClient().get('/webstore/webstore_app/config')
            .then(res => {
                if (res.data.trackingId) {
                    this.trackers.unshift({
                        trackingId: res.data.trackingId,
                        gaOptions: {
                            name: 'viaConfig'
                        }
                    });
                }

                ReactGA.initialize(this.trackers, {
                    debug: false,
                    alwaysSendToDefaultTracker: false
                });

                return this.trackers;
            });

    }

    click = (clickData = {}) => {
        if (typeof window.utag !== 'undefined' && typeof window.utag_data === 'object') {
            window.utag.link({
                ...window.utag_data,
                checkout_version: CHECKOUT_VERSION,
                ...clickData
            });
        }
    }

    view = (viewData = {}, allowedKeys = null) => {
        if (typeof window.utag !== 'undefined' && typeof window.utag_data === 'object') {
            let payload = {
                ...window.utag_data,
                checkout_version: CHECKOUT_VERSION,
                tealium_event: 'page_view',
                page_title: window.document.title,
                ...viewData
            };
            if (allowedKeys) {
                payload = _.pick(payload, allowedKeys);
            }
            window.utag.view(payload);
        }

        this.getGoogleAnalytics().then(trackers => {
            let location = `${window.location.pathname}${window.location.hash}`;
            let host = _.replace(window.location.hostname, 'www.', '');

            ReactGA.pageview(location, trackers.filter(v => v.gaOptions.name !== 'PSS').map(v => v.gaOptions.name));
            ReactGA.pageview(`${host}${location}`, ['PSS']);
        });

        return Promise.resolve();
    }

    action = (actionData = {}, allowedKeys = null) => {
        if (typeof window.utag !== 'undefined' && typeof window.utag_data === 'object') {
            let payload = {
                ...window.utag_data,
                checkout_version: CHECKOUT_VERSION,
                ...actionData
            };
            if (allowedKeys) {
                payload = _.pick(payload, allowedKeys);
            }
            window.utag.link(payload);
        }
    }

    convert = assignedOrderId => new Promise((resolve, reject) => {
        this.getGoogleAnalytics().then(trackers => {
            trackers.forEach(tracker => window.ga(`${tracker.gaOptions.name}.require`, 'ecommerce'));

            baseClient()
                .get(`/webstore/webstore_cart/get/${assignedOrderId}`)
                .then(res => {
                    trackers.forEach(tracker => window.ga(`${tracker.gaOptions.name}.ecommerce:addTransaction`, {
                        id: assignedOrderId,
                        revenue: res.data.order.order_total,
                        tax: res.data.order.tax_total,
                        shipping: res.data.order.shipping_total
                    }));

                    return res;
                })
                .then(res => Promise.all(res.data.orderItems.map(orderItem => new Promise((resolve, reject) => {
                    productsClient()
                        .getVariation(orderItem.catalog_id, orderItem.foreign_key_id)
                        .then(res => resolve({
                            ...orderItem,
                            ...res.data
                        }));
                }))))
                .then(orderItems => trackers.forEach(tracker => orderItems.forEach(orderItem => window.ga(`${tracker.gaOptions.name}.ecommerce:addItem`, {
                    id: assignedOrderId,
                    name: orderItem.ProductGroup.name,
                    sku: orderItem.manufacturer_part_number,
                    quantity: orderItem.qty
                }))))
                .then(() => trackers.forEach(tracker => window.ga(`${tracker.gaOptions.name}.ecommerce:send`)))
        })
    });
}

let Instance;
export default function () {
    if (typeof Instance === 'undefined') {
        Instance = new Analytics();
    }

    return Instance;
}